/**
 * User Level Utilities
 * 
 * Utility functions for checking and managing user level access control
 * in the dropshipping feature.
 */

export type UserLevel = 'LEVEL_0' | 'LEVEL_1' | 'LEVEL_2';

/**
 * Get user level from localStorage
 * Returns LEVEL_0 if user is not authenticated or level is not set
 */
export function getUserLevelFromStorage(): UserLevel {
    try {
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
        if (!isAuthenticated) {
            return 'LEVEL_0';
        }

        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            return 'LEVEL_0';
        }

        const user = JSON.parse(storedUser);
        const level = user.user_level as UserLevel;

        // Validate that it's a valid level
        if (['LEVEL_0', 'LEVEL_1', 'LEVEL_2'].includes(level)) {
            return level;
        }

        return 'LEVEL_0';
    } catch (error) {
        console.error('Error reading user level from storage:', error);
        return 'LEVEL_0';
    }
}

/**
 * Convert level string to numeric value for comparison
 * LEVEL_0 = 0, LEVEL_1 = 1, LEVEL_2 = 2
 */
export function getLevelNumeric(level: UserLevel): number {
    const match = level.match(/LEVEL_(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
}

/**
 * Check if user has minimum required level
 * @param userLevel - Current user's level
 * @param requiredLevel - Minimum required level
 * @returns true if user level >= required level
 */
export function hasMinimumLevel(userLevel: UserLevel, requiredLevel: UserLevel): boolean {
    return getLevelNumeric(userLevel) >= getLevelNumeric(requiredLevel);
}

/**
 * Get user level display name
 */
export function getLevelDisplayName(level: UserLevel): string {
    const levelMap: Record<UserLevel, string> = {
        'LEVEL_0': '体验用户',
        'LEVEL_1': '标准用户',
        'LEVEL_2': '高级用户'
    };
    return levelMap[level] || level;
}
